import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Search } from "lucide-react";
import api from "../lib/api";
import SEO from "../components/SEO";
import ContactCTA from "../components/ContactCTA";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function getEdgePosts() {
  if (typeof window === "undefined") return null;
  return Array.isArray(window.__TF_BLOG_POSTS__) ? window.__TF_BLOG_POSTS__ : null;
}

export default function Blogg() {
  const initialPosts = getEdgePosts();
  const [posts, setPosts] = useState(initialPosts || []);
  const [loading, setLoading] = useState(!initialPosts);
  const [q, setQ] = useState("");

  useEffect(() => {
    const edgePosts = getEdgePosts();
    if (edgePosts) {
      window.__TF_BLOG_POSTS__ = null;
      setPosts(edgePosts);
      setLoading(false);
      return;
    }

    api
      .get("/blog", { params: { limit: 60 } })
      .then((r) => setPosts(r.data.items || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = q
    ? posts.filter(
        (p) =>
          p.title?.toLowerCase().includes(q.toLowerCase()) ||
          p.excerpt?.toLowerCase().includes(q.toLowerCase())
      )
    : posts;

  return (
    <div data-testid="blogg-page" className="bg-[#F7F5F0]">
      <SEO
        title="Blogg | Trädfällarna Laholm AB"
        description="Läs senaste nytt från Trädfällarna Laholm AB. Här delar vi uppdateringar från jobb, tjänster och vad som händer just nu."
        path="/blogg"
      />

      {/* Hero */}
      <section className="relative bg-[#1B2A1E] text-white tf-grain overflow-hidden">
        <div className="absolute inset-0 opacity-25 pointer-events-none">
          <img
            src="/images/tradvard.webp"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B2A1E] via-[#1B2A1E]/95 to-[#1B2A1E]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <span className="eyebrow !text-[#F2A57C]">Blogg</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight max-w-3xl">
            Senaste från Trädfällarna
          </h1>
          <p className="mt-5 text-[#D9DCD3] text-lg leading-relaxed max-w-2xl">
            Korta uppdateringar från jobben, säsongstips och vad som händer just nu.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mb-8 relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#5A6258]" />
            <input
              data-testid="blog-search"
              type="search"
              placeholder="Sök i blogg…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full h-12 pl-11 pr-4 rounded-lg bg-white border border-[#E2E0D8] focus:outline-none focus:ring-2 focus:ring-[#D95D2E] focus:border-transparent"
              aria-label="Sök i blogg"
            />
          </div>

          {loading ? (
            <div className="text-[#5A6258]">Laddar inlägg…</div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="blog-empty"
              className="text-center py-20 bg-white rounded-xl border border-[#E2E0D8]"
            >
              <h2 className="font-heading text-xl font-bold text-[#1B2A1E] mb-2">
                Inga inlägg ännu
              </h2>
              <p className="text-[#5A6258]">
                Vi delar uppdateringar här löpande. Kom gärna tillbaka snart!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {filtered.map((p, i) => (
                <Link
                  key={p.id}
                  to={`/blogg/${p.slug}`}
                  data-testid={`blog-card-${i + 1}`}
                  className="group flex flex-col bg-white border border-[#E2E0D8] rounded-xl overflow-hidden hover:border-[#D95D2E] hover:shadow-lg transition-all"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#1B2A1E] tf-img-zoom">
                    {p.image ? (
                      <img
                        src={p.image}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1B2A1E] to-[#283224]" />
                    )}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-xs text-[#5A6258] mb-2">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(p.published_at || p.created_at)}
                    </div>
                    <h3 className="font-heading text-xl font-bold text-[#1B2A1E] mb-2 group-hover:text-[#D95D2E] transition-colors leading-snug">
                      {p.title}
                    </h3>
                    {p.excerpt && (
                      <p className="text-[#3a4338] leading-relaxed flex-1 text-[15px]">
                        {p.excerpt}
                      </p>
                    )}
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[#D95D2E] font-semibold text-sm">
                      Läs mer
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <ContactCTA />
    </div>
  );
}
